import { useQuery } from "@tanstack/react-query";
import { FlaskConical } from "lucide-react";
import { projectQuery } from "@/lib/projectsQuery";
import { cn } from "@/lib/utils";
import { useTranslation } from "@/hooks/useTranslation";

/**
 * "Test project — everything here is sample data." Above every page of a
 * project marked Test (projects.is_test_project), so nobody demoing or testing
 * one mistakes its contacts, sales or email figures for a real builder's
 * (Andrew, 2026-10-31). The four test projects are filled by
 * scripts/seed-test-projects.ts.
 *
 * Reads the project the header already reads (lib/projectsQuery), so it costs
 * no request of its own. The flask is the project picker's Test chip.
 */
export function TestProjectBanner({ projectId, className }: { projectId: string; className?: string }) {
  const t = useTranslation();
  const { data } = useQuery(projectQuery(projectId));
  if (!data?.isTestProject) return null;

  return (
    <div
      role="note"
      className={cn(
        "flex items-start gap-2.5 rounded-md border border-warning/25 bg-tint-warning px-3 py-2.5 text-on-tint-warning",
        className,
      )}
      data-testid="test-project-banner"
    >
      <FlaskConical className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
      <p className="min-w-0 flex-1 text-label">
        <span className="font-semibold">{t("shellTestProjectBannerTitle", "Test project.")}</span>{" "}
        {t("shellTestProjectBannerBody", "Everything here is sample data for demos and testing, not a real builder's buyers, sales or emails.")}
      </p>
    </div>
  );
}
